import { motion } from "framer-motion";
import { toast } from "sonner";
import { CheckCircle2, Download, ArrowLeft, Mail, Clock, Gift, Sparkles, ExternalLink } from "lucide-react";
import { useNoIndex } from "@/lib/useNoIndex";

const LOGO = "/assets/logo.png";

const DRIVE_REELS_URL = "https://drive.google.com/drive/folders/1ta8F4_S8qxuFV3YnEb-6fi0bnBQoRS3z?usp=sharing";

const BONUSES = [
  { title: "100 Hustle Mindset Reels", img: "/assets/bonus-hustle-reels.jpg", href: DRIVE_REELS_URL, type: "drive" },
  { title: "350 Content Hooks", img: "/assets/bonus-content-hooks.jpg", href: "/ebooks/350-content-hooks.pdf", type: "download" },
  { title: "200+ Luxe Life Reels", img: "/assets/bonus-luxe-woman.jpg", href: DRIVE_REELS_URL, type: "drive" },
];

const EBOOKS = [
  { title: "The Ultimate Faceless Marketing Guide", file: "/ebooks/the-ultimate-faceless-marketing-guide.pdf" },
  { title: "Faceless Youtube Automation For Passive Profits", file: "/ebooks/faceless-youtube-automation-for-passive-profits.pdf" },
  { title: "Faceless TikTok Guide for Digital Marketing", file: "/ebooks/faceless-tiktok-guide-for-digital-marketing.pdf" },
  { title: "The Ultimate Faceless Instagram Guide", file: "/ebooks/the-ultimate-faceless-instagram-guide.pdf" },
  { title: "Mastering Your Faceless Instagram Reels", file: "/ebooks/mastering-your-faceless-instagram-reels.pdf" },
  { title: "Chat GPT And Canva", file: "/ebooks/chat-gpt-and-canva.pdf" },
  { title: "Faceless Digital Marketing", file: "/ebooks/faceless-digital-marketing.pdf" },
  { title: "Mastering Etsy and Pinterest MRR", file: "/ebooks/mastering-etsy-and-pinterest-mrr.pdf" },
  { title: "Mastering The World Of Social Media", file: "/ebooks/mastering-the-world-of-social-media.pdf" },
  { title: "eMail Marketing For Beginners", file: "/ebooks/email-marketing-for-beginners.pdf" },
  { title: "TikTok Money Guide", file: "/ebooks/tiktok-money-guide.pdf" },
];

export default function ThankYou() {
  useNoIndex();
  const notReady = () => {
    toast.info("This ebook is being finalized and will be available here very soon.");
  };

  return (
    <div className="min-h-screen bg-[#FFF9F2] text-[#232323] grain relative">
      <div className="max-w-4xl mx-auto px-6 py-16">
        <div className="flex items-center gap-3 mb-16">
          <img src={LOGO} alt="The Faceless Bundle logo — faceless digital marketing ebooks to monetize social media" className="w-9 h-9 object-contain" />
          <span className="font-serif-display text-lg tracking-[0.25em] font-semibold">
            THE FACELESS BUNDLE<span className="text-[#F59E0B]">.</span>
          </span>
        </div>

        <motion.div
          data-testid="thankyou-success"
          initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          <div className="flex flex-col items-center text-center">
            <div className="w-20 h-20 flex items-center justify-center border border-[#F59E0B]/40 bg-[#F59E0B]/5">
              <CheckCircle2 className="w-10 h-10 text-[#F59E0B]" />
            </div>
            <span className="mt-6 text-sm uppercase tracking-[0.25em] text-[#F59E0B]">Payment successful</span>
            <h1 className="font-serif-display text-4xl sm:text-5xl font-bold mt-4">
              Welcome to your faceless empire
            </h1>
            <p className="mt-6 text-lg text-[#666666] max-w-xl">
              Thank you for your purchase! Your complete 11-ebook Faceless Marketing Bundle
              is ready below. Download each guide and start building today.
            </p>
          </div>

          <div className="mt-16">
            <h2 className="font-serif-display text-2xl font-semibold mb-6">Your Downloads</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {EBOOKS.map((book, i) => {
                const ready = Boolean(book.file);
                const content = (
                  <>
                    <div className="flex items-center gap-4">
                      <span className="font-serif-display text-lg text-[#8A8A8A]">{String(i + 1).padStart(2, "0")}</span>
                      <span className="text-sm font-medium">{book.title}</span>
                    </div>
                    {ready ? (
                      <Download className="w-5 h-5 text-[#666666] group-hover:text-[#F59E0B] transition-colors shrink-0" />
                    ) : (
                      <span className="flex items-center gap-1.5 text-[10px] uppercase tracking-widest text-[#8A8A8A] shrink-0">
                        <Clock className="w-3.5 h-3.5" /> Soon
                      </span>
                    )}
                  </>
                );
                return ready ? (
                  <a
                    key={i}
                    href={book.file}
                    download
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid={`download-item-${i}`}
                    className="group flex items-center justify-between gap-4 bg-[#FFFFFF] border border-[#EFE3D5] p-5 text-left hover:border-[#F59E0B]/50 transition-colors"
                  >
                    {content}
                  </a>
                ) : (
                  <button
                    key={i}
                    onClick={notReady}
                    data-testid={`download-item-${i}`}
                    className="group flex items-center justify-between gap-4 bg-[#FFFFFF]/60 border border-[#EFE3D5]/60 p-5 text-left cursor-default"
                  >
                    {content}
                  </button>
                );
              })}
            </div>

            {/* Bonuses */}
            <div className="mt-16">
              <div className="flex items-center gap-3 mb-6">
                <Gift className="w-6 h-6 text-[#F59E0B]" />
                <h2 className="font-serif-display text-2xl font-semibold">Claim Your Free Bonuses</h2>
              </div>
              <p className="text-sm text-[#666666] mb-8 max-w-xl">
                As a thank-you, these 3 exclusive bonuses are included with your order — completely free. Claim them below.
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-5" data-testid="thankyou-bonuses">
                {BONUSES.map((bonus, i) => (
                  <div
                    key={i}
                    data-testid={`bonus-claim-card-${i}`}
                    className="group relative bg-[#FFFFFF] border border-[#F59E0B]/25 overflow-hidden flex flex-col hover:border-[#F59E0B]/60 transition-colors"
                  >
                    <span className="absolute top-3 right-3 z-20 flex items-center gap-1 bg-[#F59E0B] text-black text-[10px] font-extrabold uppercase tracking-widest px-2.5 py-1">
                      <Sparkles className="w-3 h-3" /> Free
                    </span>
                    <div className="relative aspect-[4/3] bg-[#F5EFE7] overflow-hidden">
                      <img src={bonus.img} alt={bonus.title} className="w-full h-full object-contain" loading="lazy" />
                      <div
                        className="absolute inset-0 pointer-events-none"
                        style={{ background: "radial-gradient(ellipse at center, rgba(10,10,10,0) 40%, rgba(10,10,10,0.9) 100%)" }}
                      />
                    </div>
                    <div className="p-5 flex flex-col flex-1">
                      <h3 className="text-base font-semibold leading-snug">{bonus.title}</h3>
                      <a
                        href={bonus.href}
                        target="_blank"
                        rel="noopener noreferrer"
                        {...(bonus.type === "download" ? { download: true } : {})}
                        data-testid={`bonus-claim-button-${i}`}
                        className="btn-gold mt-4 w-full py-3 text-xs flex items-center justify-center gap-2"
                      >
                        {bonus.type === "drive" ? (
                          <><ExternalLink className="w-4 h-4" /> Open in Drive</>
                        ) : (
                          <><Download className="w-4 h-4" /> Download PDF</>
                        )}
                      </a>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className="mt-10 flex items-center gap-3 text-sm text-[#666666] border-t border-[#EFE3D5] pt-8">
              <Mail className="w-5 h-5 text-[#F59E0B]" />
              A copy of your download links has also been reserved for your email.
            </div>

            <a href="/" className="inline-flex items-center gap-2 mt-10 text-sm text-[#666666] hover:text-[#F59E0B] transition-colors">
              <ArrowLeft className="w-4 h-4" /> Back to store
            </a>
          </div>
        </motion.div>
      </div>
    </div>
  );
}
